/**
 * Sushi stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const sushiStickersPack: ToilePack = {
    slug: 'sushi-stickers',
    category: 'sticker',
    name: 'Sushi stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'maki', label: 'Maki roll', subject: 'a maki roll seen from the end with rice, seaweed and a cucumber centre'},
        {key: 'nigiri', label: 'Salmon nigiri', subject: 'a piece of salmon nigiri with a dab of wasabi'},
        {key: 'onigiri', label: 'Onigiri', subject: 'a rice triangle with a seaweed band and a tiny smiling face'},
    ],
    styles: [
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'doodle', label: 'Hand-drawn', style: 'hand-drawn marker doodle sticker, loose line, flat wash fills', palette: 'Two or three muted colours with a black line', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a dumpling instead', 'for a lunchbox', 'as a bento layout'],
    directionHint: 'try: a dumpling instead',
    seo: {
        title: 'Sushi Stickers: Maki, Nigiri, Onigiri',
        metaDescription: 'Nine kawaii sushi stickers — a maki roll, salmon nigiri, an onigiri with a face — in kawaii, bold cartoon, and hand-drawn styles.',
        h1: 'Sushi stickers: maki roll, salmon nigiri, onigiri',
        tagline: 'Nine stickers, three sushi pieces, three styles each.',
        intro: 'A maki roll seen end-on, a piece of salmon nigiri with a dab of wasabi, and a rice-triangle onigiri with a tiny smiling face — three sushi stickers, die-cut and ready to print. Each rendered kawaii, bold cartoon, and loose hand-drawn. Ask for a different sushi type, a bento-box scene, or a chopsticks composition, and the same three styles carry it.',
        useCases: [
            'a water bottle or lunchbox',
            'a planner or journal page',
            'a phone case sticker sheet',
            'a kitchen or restaurant sign',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine sushi stickers — a maki roll, a piece of salmon nigiri, and an onigiri — in kawaii, bold cartoon, and hand-drawn finishes.'},
            {q: 'Can I get a different sushi type?', a: 'Yes — the direction field takes any variation, including a different sushi type, a bento-box scene, or a chopsticks composition.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a full A4 sheet.'},
            {q: 'Are these photos of real sushi?', a: 'No — every sticker is an original kawaii-style illustration, not a photograph.'},
        ],
    },
}
